import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

/**
 * First-visit intro (ported from the v2 rebuild). The logo settles over a dark
 * field, an accent hairline draws, then two panels part like doors to reveal
 * the site. Shows once per session (sessionStorage), and is skipped entirely
 * under prefers-reduced-motion.
 */
const SEEN_KEY = 'tla_intro_seen'
const doorEase = [0.83, 0, 0.17, 1] as const
const settleEase = [0.16, 1, 0.3, 1] as const

export default function IntroVeil() {
  const [show, setShow] = useState(true)

  useEffect(() => {
    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches
    let seen = false
    try {
      seen = sessionStorage.getItem(SEEN_KEY) === '1'
    } catch {
      /* sessionStorage may be unavailable */
    }
    if (reduced || seen) {
      setShow(false)
      return
    }
    try {
      sessionStorage.setItem(SEEN_KEY, '1')
    } catch {
      /* ignore */
    }
    const id = setTimeout(() => setShow(false), 3400)
    return () => clearTimeout(id)
  }, [])

  if (!show) return null

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[200] overflow-hidden"
    >
      {/* Left door */}
      <motion.div
        className="absolute inset-y-0 left-0 w-1/2 bg-garage-dark"
        initial={{ x: 0 }}
        animate={{ x: '-101%' }}
        transition={{ duration: 1.15, delay: 1.7, ease: doorEase }}
      />
      {/* Right door */}
      <motion.div
        className="absolute inset-y-0 right-0 w-1/2 bg-garage-dark"
        initial={{ x: 0 }}
        animate={{ x: '101%' }}
        transition={{ duration: 1.15, delay: 1.7, ease: doorEase }}
        onAnimationComplete={() => setShow(false)}
      />
      {/* Center seam hairline */}
      <motion.div
        className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-garage-accent/60 to-transparent"
        initial={{ opacity: 0, scaleY: 0.6 }}
        animate={{ opacity: [0, 0.9, 0], scaleY: [0.6, 1, 1] }}
        transition={{ duration: 1.5, delay: 1.5, ease: 'easeOut' }}
      />
      {/* Logo + hairline */}
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0] }}
        transition={{
          duration: 2,
          times: [0, 0.3, 0.65, 0.85],
          ease: 'easeInOut',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.92 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.4, ease: settleEase }}
        >
          <span className="flex h-36 w-36 items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_10px_40px_rgba(0,0,0,0.5)] sm:h-44 sm:w-44">
            <img
              src="/logo-white.png"
              alt=""
              width={176}
              height={176}
              className="h-full w-full"
            />
          </span>
        </motion.div>
        <motion.div
          className="mt-4 h-px w-16 bg-garage-accent/70"
          style={{ transformOrigin: 'center' }}
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 1, delay: 0.6, ease: settleEase }}
        />
      </motion.div>
    </div>
  )
}
